import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { UtilityService } from '@eforall/common';
import { DialogService, GridContentWidget, PageTabsMainTabGridSize } from '@eforall/dashboard';
import { AccSessionTopicSurveyResponseFuncData, AccTopicSpecialistFuncData, Event } from '@interfaces';
import { DomainDataService } from '../../../../../../services';
import { AcceleratorAreaService } from '../../../accelerator-area.service';
import { AccDeliveryService } from '../../acc-delivery.service';
import { getSetup } from './grid/get-setup';

export interface TopicRow {
	topicId: number,
	accTopicId: number,
	needsSpecialist: boolean,
	accSessionId: number | undefined,
	sessionStartUTC?: number,
	specialists: AccTopicSpecialistFuncData[],
	specialistNames: string,
	topicName: string,
	accTopicSpecialistIds: number[],
	hasSpecialistOrSession: boolean,
	materialsCount: number,
	topicSurveyResults: number | undefined,
	topicSurveyRelevanceScore: number | undefined,
	topicSurveyNeedScore: number | undefined,
	topicSurveyUnderstandingScore: number | undefined,
	topicSurveyDurationScore: number | undefined,
	displayOrder: number,
	updatedUTC: number,
}

@Component({
	selector: 'acc-topics-view-part',
	standalone: true,
	imports: [GridContentWidget],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './topics-view.part.html',
})
export class AccTopicsViewPart {

	private readonly util = inject(UtilityService);
	private readonly pageService = inject(AccDeliveryService);
	private readonly areaService = inject(AcceleratorAreaService);
	private readonly domain = inject(DomainDataService);
	private readonly dialogService = inject(DialogService);

	public setup = getSetup(this.util, this.dialogService, this.areaService);

	public pageTabsGridSize = computed<PageTabsMainTabGridSize | undefined>(() => {
		if (this.pageService.layout()) return { layout: this.pageService.layout()!, viewSelector: true };
		return undefined;
	});

	public topics = computed<TopicRow[]>(() => {
		const data = this.areaService.data();
		if (!data) return [];

		const sessions = data.sessions;
		const sessionTopics = data.sessionTopics;
		const accTopics = data.accTopics;
		const topicSpecialists = data.topicSpecialists;

		const ddTopics = this.domain.data().topics;
		const ddTopicMap = this.util.array.toMap(ddTopics, topic => topic.topicId);

		const topicResponses = data.topicSurveyResponses;


		const topicMaterials = data.topicMaterials;
		const rows: TopicRow[] = [];
		for (const accTopic of accTopics) {

			const ddTopic = ddTopicMap[accTopic.topicId];
			// If there is no domain topic for this accTopic, skip it completely
			if (!ddTopic) continue;

			const specialists: AccTopicSpecialistFuncData[] = [];
			let specialistNames = 'n/a';

			if (ddTopic.needsSpecialist) {
				specialists.push(...topicSpecialists.filter(specialist => specialist.accTopicId == accTopic.accTopicId));
				specialistNames = specialists.map(sp => sp.personName).sort((a, b) => a > b ? 1 : -1).join(', ');
			}

			const sessionTopic = sessionTopics.find(sessionTopic => sessionTopic.accTopicId == accTopic.accTopicId);
			const session = sessionTopic ? sessions.find(session => session.accSessionId == sessionTopic.accSessionId) : undefined;
			const event = session ? data.events.find(e => e.eventId == session.eventId) : undefined;


			//
			// Get all the topic responses for this topic and this session
			//
			const allTopicResponses: AccSessionTopicSurveyResponseFuncData[] = topicResponses.filter(r => r.topicId == accTopic.topicId);

			const r: TopicRow = {
				accTopicId: accTopic.accTopicId,
				topicId: accTopic.topicId,
				needsSpecialist: ddTopic.needsSpecialist,
				materialsCount: topicMaterials.filter(material => accTopic.topicId == material.topicId).length || 0,
				accSessionId: session?.accSessionId,
				sessionStartUTC: event?.startUTC,
				accTopicSpecialistIds: specialists?.map(s => s.personId) || [],
				topicName: ddTopic?.name || 'n/a',
				specialists,
				specialistNames,
				displayOrder: ddTopic.order,
				hasSpecialistOrSession: !!specialists.length || !!session,
				topicSurveyResults: allTopicResponses.length || undefined,
				topicSurveyRelevanceScore: this.util.nps.calcScore(allTopicResponses.map(result => result.relevance)) || undefined,
				topicSurveyNeedScore: this.util.nps.calcScore(allTopicResponses.map(result => result.need)) || undefined,
				topicSurveyUnderstandingScore: this.util.nps.calcScore(allTopicResponses.map(result => result.understanding)) || undefined,
				topicSurveyDurationScore: this.util.nps.calcScore(allTopicResponses.map(result => result.duration)) || undefined,
				updatedUTC: data.asOfUTC,
			};

			rows.push(r);

		}

		return rows.sort((a, b) => a.displayOrder > b.displayOrder ? 1 : -1);

	});
}